/**
 * Auto-recovery for stale dynamic-import chunks after a deploy.
 *
 * Vite gir hver lazy-lastet chunk et innholds-hash-navn (`Map3DView-<hash>.js`).
 * Ved en ny hosting-deploy endres hashene og de gamle filnavnene fjernes. En
 * fane som fortsatt kjører forrige versjon peker på det gamle filnavnet; når
 * brukeren så trigger `import()` (f.eks. åpner 3D-kartet), 404-er filen og
 * nettleseren kaster «Failed to fetch dynamically imported module». Uten
 * håndtering bobler dette til ErrorBoundary og lander som en «crash» i
 * error_reports (ekte tilfelle: doc error_reports/YEkFOGfPpPft9fULyYuG,
 * Map3DView-D98MOwRB.js, v1.0.302).
 *
 * Vite sender `vite:preloadError` når dette skjer. Fiksen: last siden på nytt
 * én gang for å hente fersk index.html + nye chunks. Reloaden er beskyttet av et
 * sessionStorage-tidsstempel slik at en genuint manglende fil (ødelagt bygg)
 * ikke fanger brukeren i en reload-løkke — da får feilen boble som normalt og
 * blir synlig i admin-panelet.
 */

export const RELOAD_GUARD_KEY = 'fricamp:preloadReloadAt';
export const RELOAD_GUARD_WINDOW_MS = 30_000;

/**
 * Ren beslutning: skal vi reloade nå, gitt forrige reload-tidsstempel (rått fra
 * sessionStorage) og nåtid? Reload hvis vi ikke har reloadet før, hvis stempelet
 * er ugyldig, eller hvis forrige reload var utenfor guard-vinduet. Blokker (false)
 * bare når vi nettopp reloadet — det bryter løkka ved et ekte manglende asset.
 */
export function shouldReloadOnPreloadError(nowMs: number, lastReloadRaw: string | null): boolean {
  if (!lastReloadRaw) return true;
  const last = Number(lastReloadRaw);
  if (!Number.isFinite(last)) return true;
  return nowMs - last >= RELOAD_GUARD_WINDOW_MS;
}

/**
 * Installer `vite:preloadError`-lytteren. Kall én gang tidlig i oppstart.
 * `reload` er injiserbar for test — default reloader nettleseren.
 */
export function installPreloadErrorReload(reload: () => void = () => window.location.reload()): void {
  window.addEventListener('vite:preloadError', () => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    const now = Date.now();
    const last = storage ? storage.getItem(RELOAD_GUARD_KEY) : null;
    if (!shouldReloadOnPreloadError(now, last)) return;
    try {
      storage?.setItem(RELOAD_GUARD_KEY, String(now));
    } catch {
      /* ignore — private mode / storage full */
    }
    reload();
  });
}
